import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  TextField,
  Button,
  Box,
  Typography,
  Modal,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Grid,
  IconButton,
  Chip,
  InputAdornment,
  Alert,
} from "@mui/material";
import BaseAutoComplete from "../../Base/AutoComplete";
import ownerService from "../../../services/owner.service";
import { IOwnerObject } from "../../../services/api.types";
import {
  ArrowLeft,
  ArrowRight,
  Close,
  LocationOn,
  Business,
  AttachMoney,
  PhotoCamera,
  AddAPhoto,
  PhotoLibrary,
} from "@mui/icons-material";
import { STATUS_SUCCESS } from "../../../constants/responseTypes";
import { YMaps, Map } from "@pbe/react-yandex-maps";
import { createCarWash } from "../../../services/carWash.service";
import { AlertContext, AlertContextType } from "../../../context/AlertContext";
import WebcamCapture from "../../WebCam/Webcam";

// i18n.t is typed with incompatible overloads in the installed react-i18next version
const _t = (key: string, opts?: Record<string, unknown>): string =>
  (i18n.t as any)(key, opts);

// Client-side sanity limits, mirroring the backend's multer config in
// carWashController.js (`FILE_SIZE_LIMIT` default 5MB, `MAX_FILE_COUNT`
// default 10, and an `image/*` fileFilter).
// UX ONLY — the server is the enforcement point for type, size and count.
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_COUNT = 10;

type Props = {
  ownerID?: string;
  isOpen: boolean;
  closeModal: () => void;
  lockedOwner?: boolean;
};

// Updated validation schema with minivan
const carWashValidationSchema = Yup.object().shape({
  legalName: Yup.string().required(_t("validation.legalNameRequired")),
  owner: Yup.string().required(_t("validation.ownerRequired")),
  parkingDescription: Yup.string(),
  location: Yup.object().shape({
    latitude: Yup.string().required(_t("validation.latitudeRequired")),
    longitude: Yup.string().required(_t("validation.longitudeRequired")),
    street: Yup.string().required(_t("validation.streetRequired")),
    description: Yup.string(),
  }),
  services: Yup.object().shape({
    sedan: Yup.object().shape({
      inner: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
      outer: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
      both: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
    }),
    cuv: Yup.object().shape({
      inner: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
      outer: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
      both: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
    }),
    suv: Yup.object().shape({
      inner: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
      outer: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
      both: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
    }),
    minivan: Yup.object().shape({
      inner: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
      outer: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
      both: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
    }),
    van: Yup.object().shape({
      inner: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
      outer: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
      both: Yup.number().required().min(0, _t("validation.priceMustBeNonNegative")),
    }),
  }),
  images: Yup.array().min(1, _t("validation.imageRequired")),
});

// TODO: Fix this
const initialCarWashObject: any = {
  legalName: "",
  owner: "",
  contact: {
    fullName: "",
    phone: "",
  },
  parkingDescription: "",
  location: {
    latitude: "",
    longitude: "",
    street: "",
    description: "",
  },
  services: {
    sedan: {
      inner: 0,
      outer: 0,
      both: 0,
    },
    cuv: {
      inner: 0,
      outer: 0,
      both: 0,
    },
    suv: {
      inner: 0,
      outer: 0,
      both: 0,
    },
    minivan: {
      inner: 0,
      outer: 0,
      both: 0,
    },
    van: {
      inner: 0,
      outer: 0,
      both: 0,
    },
  },
  images: [],
};

const steps = [
  { labelKey: "forms.carWash.stepBasicInfo", icon: <Business /> },
  { labelKey: "forms.carWash.stepPricing", icon: <AttachMoney /> },
  { labelKey: "forms.carWash.stepLocation", icon: <LocationOn /> },
  { labelKey: "forms.carWash.stepImages", icon: <PhotoCamera /> },
];

const VEHICLE_TYPE_LABEL_KEYS: Record<string, string> = {
  sedan: "vehicle.sedan",
  cuv: "vehicle.cuv",
  suv: "vehicle.suv",
  minivan: "vehicle.minivan",
  van: "vehicle.van",
};

const SERVICE_OPTION_LABEL_KEYS: Record<string, string> = {
  inner: "vehicle.innerWash",
  outer: "vehicle.outerWash",
  both: "vehicle.fullWash",
};

const appendCarWashToFormData = (carWashObject: any): FormData => {
  const formData = new FormData();

  formData.append("legalName", carWashObject.legalName || "");
  formData.append("owner", carWashObject.owner || "");
  formData.append("location", JSON.stringify(carWashObject.location));
  formData.append("services", JSON.stringify(carWashObject.services));
  if (carWashObject.parkingDescription) {
    formData.append("parkingDescription", carWashObject.parkingDescription);
  }

  carWashObject.images.forEach((image: File) => {
    formData.append("images", image);
  });

  return formData;
};

// Recursively collect all leaf error strings from a (possibly nested) Formik
// errors object.
const collectErrorMessages = (obj: any, acc: string[] = []): string[] => {
  if (!obj) return acc;
  if (typeof obj === "string") {
    acc.push(obj);
    return acc;
  }
  Object.values(obj).forEach((v) => collectErrorMessages(v, acc));
  return acc;
};

// Convert a Formik errors object into a matching-shape touched object (all
// leaves set to true) so inline field errors become visible after a failed
// submit attempt.
const errorsToTouched = (obj: any): any => {
  if (typeof obj === "string") return true;
  if (Array.isArray(obj)) return obj.map(errorsToTouched);
  const out: any = {};
  Object.keys(obj).forEach((k) => {
    out[k] = errorsToTouched(obj[k]);
  });
  return out;
};

// Map a top-level errors key to the wizard step that owns it, so we can jump
// the user to the first step containing an invalid field.
const stepForErrorKey = (key: string): number | null => {
  if (key === "legalName" || key === "owner") return 0;
  if (key === "services") return 1;
  if (key === "location") return 2;
  if (key === "images") return 3;
  return null;
};

const base64ToFile = (base64String: string, fileName: string) => {
  const arr = base64String.split(",");
  const mimeType = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg";
  const base64 = arr[1];
  const byteString = atob(base64);
  const byteNumbers = new Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    byteNumbers[i] = byteString.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  return new File([blob], fileName, { type: mimeType });
};

const CarWashForm: React.FC<Props> = ({
  ownerID,
  isOpen,
  closeModal,
  lockedOwner,
}) => {
  const { t } = useTranslation();
  const { setAlert } = useContext<AlertContextType | undefined>(AlertContext)!;
  const [owners, setOwners] = useState<IOwnerObject[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const formik = useFormik({
    initialValues: initialCarWashObject,
    validationSchema: carWashValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setIsSubmitting(true);
      try {
        const formData = appendCarWashToFormData(values);
        const res = await createCarWash(formData);

        if (res.status === STATUS_SUCCESS) {
          closeModal();
          setAlert("success", t("carWashes.createdSuccess"));
          formik.resetForm();
          setImagePreviews([]);
          setActiveStep(0);
        }
      } catch (err: any) {
        setAlert("error", err.message || t("carWashes.createFailed"));
      } finally {
        setIsSubmitting(false);
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (ownerID && !formik.values.owner) {
      formik.setFieldValue("owner", ownerID);
    }
    handleGetOwners();
    // Intentionally re-runs only when ownerID changes; formik and
    // handleGetOwners are recreated every render (formik is not memoized,
    // handleGetOwners is a plain function), so adding them here would
    // refetch owners on every render instead of only when the owner param
    // changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerID]);

  const handleGetOwners = async () => {
    try {
      const res = await ownerService.getOwners();
      setOwners(res.data);
    } catch (err) {
      console.error("Failed to fetch owners:", err);
      setAlert("error", t("forms.carWash.loadOwnersFailed"));
    }
  };

  const handleTakeAPicture = useCallback(
    (imgSrc: string) => {
      const file = base64ToFile(imgSrc, `webcam_${Date.now()}.jpeg`);
      formik.setFieldValue("images", [...formik.values.images, file]);
      setImagePreviews((prev) => [...prev, URL.createObjectURL(file)]);
    },
    [formik]
  );

  // Gallery / file-system picker. Mirrors handleTakeAPicture: the selected
  // Files go straight into formik.values.images (later appended to the
  // multipart FormData under the `images` field the backend's
  // upload.array("images") expects) and an object URL is kept for preview.
  const handleFilesSelected = useCallback(
    (files: File[]) => {
      const current: File[] = formik.values.images;
      const accepted: File[] = [];
      let rejectedType = false;
      let rejectedSize = false;

      files.forEach((file) => {
        if (!file.type.startsWith("image/")) {
          rejectedType = true;
          return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
          rejectedSize = true;
          return;
        }
        accepted.push(file);
      });

      const room = Math.max(MAX_IMAGE_COUNT - current.length, 0);
      const toAdd = accepted.slice(0, room);
      const overflow = accepted.length > toAdd.length;

      if (toAdd.length > 0) {
        formik.setFieldValue("images", [...current, ...toAdd]);
        setImagePreviews((prev) => [
          ...prev,
          ...toAdd.map((file) => URL.createObjectURL(file)),
        ]);
      }

      const messages: string[] = [];
      if (rejectedType) messages.push(t("forms.carWash.imageInvalidType"));
      if (rejectedSize)
        messages.push(
          t("forms.carWash.imageTooLarge", {
            size: MAX_IMAGE_BYTES / (1024 * 1024),
          })
        );
      if (overflow)
        messages.push(
          t("forms.carWash.imageMaxCount", { count: MAX_IMAGE_COUNT })
        );
      if (messages.length > 0) setAlert("error", messages.join(" • "));
    },
    [formik, setAlert, t]
  );

  const handleImageDelete = useCallback(
    (index: number) => {
      const removed = imagePreviews[index];
      if (removed) URL.revokeObjectURL(removed);
      const updatedPreviews = imagePreviews.filter((_, i) => i !== index);
      setImagePreviews(updatedPreviews);
      const updatedFiles = formik.values.images.filter(
        (_: any, i: number) => i !== index
      );
      formik.setFieldValue("images", updatedFiles);
    },
    [imagePreviews, formik]
  );

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };

  // Final-step submit. Previously the button called formik.handleSubmit()
  // directly; when full-schema validation failed, Formik silently did nothing
  // (onSubmit never ran, no feedback) — the "Create does nothing" symptom.
  // Now we validate first, surface any errors via the alert, mark fields
  // touched, and jump to the first offending step. Only submit when valid.
  const handleFinalSubmit = async () => {
    const errors = await formik.validateForm();
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      formik.setTouched(errorsToTouched(errors), false);
      const firstStep = stepForErrorKey(errorKeys[0]);
      if (firstStep !== null) setActiveStep(firstStep);
      const messages = Array.from(new Set(collectErrorMessages(errors)));
      setAlert(
        "error",
        messages.length > 0
          ? messages.join(" • ")
          : t("carWashes.createFailed")
      );
      return;
    }
    formik.handleSubmit();
  };

  const isStepValid = (step: number) => {
    switch (step) {
      case 0:
        return formik.values.legalName && formik.values.owner;
      case 1:
        return true; // Services validation is handled in form submission
      case 2:
        return (
          formik.values.location.latitude && formik.values.location.longitude
        );
      case 3:
        return formik.values.images.length > 0;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <BasicInfoStep
            formik={formik}
            owners={owners}
            lockedOwner={lockedOwner}
          />
        );
      case 1:
        return <PricingStep formik={formik} />;
      case 2:
        return <LocationStep formik={formik} />;
      case 3:
        return (
          <ImagesStep
            formik={formik}
            imagePreviews={imagePreviews}
            handleImageDelete={handleImageDelete}
            handleTakeAPicture={handleTakeAPicture}
            handleFilesSelected={handleFilesSelected}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={closeModal}
      aria-labelledby="car-wash-modal-title"
      aria-describedby="car-wash-modal-description"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper
        sx={{
          width: "90%",
          maxWidth: 800,
          maxHeight: "90vh",
          overflow: "auto",
          position: "relative",
          // Android (and some mobile) browsers can fail to render the outlined
          // input's <legend> notch, leaving the border visible behind the
          // floating label. Giving the shrunk label a solid background that
          // matches the surface masks the border regardless of the notch.
          "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
            backgroundColor: "background.paper",
            paddingInline: "4px",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" component="h2" fontWeight="bold">
            {t("forms.carWash.addNewCarWash")}
          </Typography>
          <IconButton onClick={closeModal} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Stepper */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.labelKey}>
                <StepLabel
                  icon={step.icon}
                  onClick={() => handleStepClick(index)}
                  sx={{ cursor: "pointer" }}
                >
                  {t(step.labelKey)}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Form Content */}
        <Box sx={{ p: 3, pt: 0 }}>
          <form onSubmit={formik.handleSubmit}>
            {renderStepContent()}

            {/* Navigation Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 3,
                pt: 2,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowLeft />}
                disabled={activeStep === 0}
                onClick={handleBack}
              >
                {t("forms.carWash.previous")}
              </Button>

              <Button
                variant="contained"
                endIcon={
                  activeStep === steps.length - 1 ? undefined : <ArrowRight />
                }
                onClick={
                  activeStep === steps.length - 1
                    ? handleFinalSubmit
                    : handleNext
                }
                disabled={isSubmitting || !isStepValid(activeStep)}
              >
                {activeStep === steps.length - 1
                  ? t("common.create")
                  : t("forms.carWash.next")}
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>
    </Modal>
  );
};

// Basic Info Step Component
const BasicInfoStep = ({
  formik,
  owners,
  lockedOwner,
}: {
  formik: any;
  owners: IOwnerObject[];
  lockedOwner?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t("carWashes.basicInfo")}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box>
          <BaseAutoComplete
            options={owners.map((owner: IOwnerObject) => ({
              id: owner._id,
              label: `${owner.firstName} ${owner.lastName} - ${
                owner.phone || owner.contact?.phone
              }`,
              ...owner,
            }))}
            formik={formik}
            label={t("forms.carWash.owner")}
            name="owner"
            disabled={lockedOwner}
          />
        </Box>

        <Box>
          <TextField
            fullWidth
            id="legalName"
            name="legalName"
            label={t("forms.carWash.legalName")}
            value={formik.values.legalName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.legalName && Boolean(formik.errors.legalName)}
            helperText={formik.touched.legalName && formik.errors.legalName}
          />
        </Box>

      </Box>
    </Box>
  );
};

// Pricing Step Component
const PricingStep = ({ formik }: { formik: any }) => {
  const { t } = useTranslation();
  const serviceTypes = ["sedan", "cuv", "suv", "minivan", "van"];
  const serviceOptions = ["inner", "outer", "both"];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t("forms.carWash.servicePricing")}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {serviceTypes.map((serviceType) => (
          <Box key={serviceType}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {VEHICLE_TYPE_LABEL_KEYS[serviceType]
                    ? t(VEHICLE_TYPE_LABEL_KEYS[serviceType])
                    : serviceType}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {serviceOptions.map((service) => (
                    <Box key={service} sx={{ flex: "1 1 30%", minWidth: 200 }}>
                      <TextField
                        fullWidth
                        id={`services.${serviceType}.${service}`}
                        name={`services.${serviceType}.${service}`}
                        label={
                          SERVICE_OPTION_LABEL_KEYS[service]
                            ? t(SERVICE_OPTION_LABEL_KEYS[service])
                            : service
                        }
                        type="number"
                        value={
                          formik.values.services[serviceType][service] || ""
                        }
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.services?.[serviceType]?.[service] &&
                          Boolean(
                            formik.errors.services?.[serviceType]?.[service]
                          )
                        }
                        helperText={
                          formik.touched.services?.[serviceType]?.[service] &&
                          formik.errors.services?.[serviceType]?.[service]
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₼</InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Location Step Component
const LocationStep = ({ formik }: { formik: any }) => {
  const { t } = useTranslation();
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
  const mapRef = useRef<any>(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const latitude =
          formik.values.location.latitude || position.coords.latitude;
        const longitude =
          formik.values.location.longitude || position.coords.longitude;
        const coords: [number, number] = [latitude, longitude];

        setMapCenter(coords);

        if (
          !formik.values.location.latitude ||
          !formik.values.location.longitude
        ) {
          handleSetLocation(latitude, longitude);
        }
      });
    }
    // Mount-only: this gets the device's current position once.
    // handleSetLocation itself updates
    // formik.values.location.latitude/longitude, so including those (or
    // handleSetLocation, which closes over them) would re-trigger this
    // effect after every location update and loop indefinitely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetLocation = async (latitude: number, longitude: number) => {
    try {
      const street = await getStreetName(latitude, longitude);
      formik.setFieldValue("location.street", street);
      formik.setFieldValue("location.latitude", latitude.toString());
      formik.setFieldValue("location.longitude", longitude.toString());
    } catch (error) {
      console.error("Failed to get street name:", error);
    }
  };

  const handleMapMove = () => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      const latitude = parseFloat(center[0].toFixed(7));
      const longitude = parseFloat(center[1].toFixed(7));
      handleSetLocation(latitude, longitude);
    }
  };

  const getStreetName = async (latitude: number, longitude: number) => {
    try {
      const res = await fetch(
        `https://geocode-maps.yandex.ru/1.x/?apikey=${process.env.REACT_APP_YANDEX_MAP_API_KEY}&geocode=${longitude},${latitude}&lang=en_US&format=json`
      );
      const data = await res.json();
      return (
        data.response.GeoObjectCollection.featureMember[0]?.GeoObject
          ?.metaDataProperty?.GeocoderMetaData?.text || "Unknown street"
      );
    } catch (error) {
      return "Unknown street";
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t("forms.carWash.locationDetails")}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={6}>
          <TextField
            fullWidth
            id="location.latitude"
            name="location.latitude"
            label={t("forms.carWash.latitude")}
            value={formik.values.location.latitude}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.location?.latitude &&
              Boolean(formik.errors.location?.latitude)
            }
            helperText={
              formik.touched.location?.latitude &&
              formik.errors.location?.latitude
            }
          />
        </Grid>

        <Grid size={6}>
          <TextField
            fullWidth
            id="location.longitude"
            name="location.longitude"
            label={t("forms.carWash.longitude")}
            value={formik.values.location.longitude}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.location?.longitude &&
              Boolean(formik.errors.location?.longitude)
            }
            helperText={
              formik.touched.location?.longitude &&
              formik.errors.location?.longitude
            }
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            id="location.street"
            name="location.street"
            label={t("forms.carWash.street")}
            value={formik.values.location.street}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.location?.street &&
              Boolean(formik.errors.location?.street)
            }
            helperText={
              formik.touched.location?.street &&
              formik.errors.location?.street
            }
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            id="location.description"
            name="location.description"
            label={t("forms.carWash.locationDescription")}
            multiline
            rows={3}
            value={formik.values.location.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.location?.description &&
              Boolean(formik.errors.location?.description)
            }
            helperText={
              formik.touched.location?.description &&
              formik.errors.location?.description
            }
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            id="parkingDescription"
            name="parkingDescription"
            label={t("forms.carWash.parkingDescription")}
            multiline
            rows={3}
            value={formik.values.parkingDescription}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={t("forms.carWash.parkingHint")}
          />
        </Grid>

        <Grid size={12}>
          <Card variant="outlined">
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
              >
                <Typography variant="subtitle1">
                  {t("forms.carWash.mapLocation")}
                </Typography>
                <Chip
                  icon={<LocationOn />}
                  label={t("forms.carWash.mapHint")}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("forms.carWash.mapDragHint")}
              </Typography>
              <Box
                sx={{ height: 350, width: "100%", position: "relative", mt: 2 }}
              >
                <YMaps
                  query={{ apikey: process.env.REACT_APP_YANDEX_MAP_API_KEY }}
                >
                  {mapCenter && (
                    <Map
                      defaultState={{ center: mapCenter, zoom: 18 }}
                      width="100%"
                      height="100%"
                      instanceRef={mapRef}
                      onActionEnd={handleMapMove}
                    />
                  )}
                </YMaps>
                {/* Fixed centered pin overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -100%)",
                    pointerEvents: "none",
                    zIndex: 1000,
                  }}
                >
                  <LocationOn
                    sx={{
                      fontSize: 40,
                      color: "error.main",
                      filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.3))",
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Images Step Component (gallery / file-system picker + camera capture)
const ImagesStep = ({
  formik,
  imagePreviews,
  handleImageDelete,
  handleTakeAPicture,
  handleFilesSelected,
}: {
  formik: any;
  imagePreviews: string[];
  handleImageDelete: (index: number) => void;
  handleTakeAPicture: (imgSrc: string) => void;
  handleFilesSelected: (files: File[]) => void;
}) => {
  const { t } = useTranslation();
  const [cameraVisible, setCameraVisible] = useState(false);

  const handleCaptureClick = (imgSrc: string) => {
    handleTakeAPicture(imgSrc);
    setCameraVisible(false);
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files ?? []);
    // Reset immediately so re-selecting the same file still fires `change`.
    event.target.value = "";
    if (files.length === 0) return;
    handleFilesSelected(files);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t("forms.carWash.carWashImages")}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                {t("forms.carWash.addImages")}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    component="label"
                    startIcon={<PhotoLibrary />}
                    sx={{ height: 120, flexDirection: "column" }}
                  >
                    <Typography variant="body2">
                      {t("forms.carWash.chooseFromGallery")}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {t("forms.carWash.fromDevice")}
                    </Typography>
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*"
                      onChange={handleFileInputChange}
                    />
                  </Button>
                </Grid>
                <Grid size={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setCameraVisible(true)}
                    startIcon={<AddAPhoto />}
                    sx={{ height: 120, flexDirection: "column" }}
                  >
                    <Typography variant="body2">
                      {t("forms.carWash.takePhoto")}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {t("forms.carWash.useCamera")}
                    </Typography>
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {imagePreviews.length > 0 && (
          <Grid size={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {t("forms.carWash.imagePreviews", {
                    count: imagePreviews.length,
                  })}
                </Typography>
                <Grid container spacing={2}>
                  {imagePreviews.map((image, index) => (
                    <Grid size={6} key={index}>
                      <Box
                        sx={{
                          position: "relative",
                          cursor: "pointer",
                          "&:hover .delete-overlay": {
                            opacity: 1,
                          },
                        }}
                        onClick={() => handleImageDelete(index)}
                      >
                        <img
                          src={image}
                          alt={t("forms.carWash.previewAlt", {
                            index: index + 1,
                          })}
                          style={{
                            width: "100%",
                            height: 120,
                            objectFit: "cover",
                            borderRadius: 8,
                          }}
                        />
                        <Box
                          className="delete-overlay"
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0,0,0,0.5)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 8,
                            opacity: 0,
                            transition: "opacity 0.2s",
                          }}
                        >
                          <Chip
                            label={t("common.remove")}
                            color="error"
                            size="small"
                            icon={<Close />}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {imagePreviews.length === 0 && (
          <Grid size={12}>
            <Alert severity="info">
              {t("forms.carWash.atLeastOneImage")}
            </Alert>
          </Grid>
        )}
      </Grid>

      {cameraVisible && <WebcamCapture handleSetImage={handleCaptureClick} />}
    </Box>
  );
};

export default CarWashForm;
